import { useState, useEffect } from 'react';
import { useToast } from '../context/ToastContext';
import { ap as apApi } from '../services/api';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    FileText,
    Check,
    X,
    Clock,
    User,
    Calendar,
    ChevronDown,
    ChevronUp,
    Loader2,
    MessageSquare,
    Filter,
    RefreshCw,
    Search
} from 'lucide-react';

const AsuntosPropiosAdmin = () => {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [pendientes, setPendientes] = useState([]);
    const [allSolicitudes, setAllSolicitudes] = useState([]);
    // Use 2026 as default since current calendar shows Jan 2026
    const [selectedYear, setSelectedYear] = useState(2026);
    const [filter, setFilter] = useState('pendientes'); // 'pendientes', 'todas', 'concedidas', 'denegadas'
    const [respondingId, setRespondingId] = useState(null);
    const [showNotaModal, setShowNotaModal] = useState(false);
    const [currentAction, setCurrentAction] = useState(null); // { id, action: 'conceder'|'denegar' }
    const [notaAdmin, setNotaAdmin] = useState('');
    const [expandedConductor, setExpandedConductor] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadData();
    }, [selectedYear]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [pendientesData, allData] = await Promise.all([
                apApi.admin.getPendientes(),
                apApi.admin.getAll(selectedYear)
            ]);
            setPendientes(pendientesData);
            setAllSolicitudes(allData);
        } catch (err) {
            toast.error('Error al cargar solicitudes: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleResponder = async (id, accion, nota = '') => {
        setRespondingId(id);
        try {
            await apApi.admin.responder(id, accion, nota);
            toast.success(`Solicitud ${accion === 'conceder' ? 'concedida' : 'denegada'}`);
            loadData();
            setShowNotaModal(false);
            setNotaAdmin('');
            setCurrentAction(null);
        } catch (err) {
            toast.error('Error: ' + err.message);
        } finally {
            setRespondingId(null);
        }
    };

    const openNotaModal = (id, action) => {
        setCurrentAction({ id, action });
        setNotaAdmin('');
        setShowNotaModal(true);
    };

    const formatFecha = (fecha) => {
        try {
            return format(parseISO(fecha), "EEEE d 'de' MMMM", { locale: es });
        } catch {
            return fecha;
        }
    };

    const getFilteredSolicitudes = () => {
        if (filter === 'pendientes') return pendientes;
        if (filter === 'concedidas') return allSolicitudes.filter(s => s.estado === 'concedido');
        if (filter === 'denegadas') return allSolicitudes.filter(s => s.estado === 'denegado');
        return allSolicitudes;
    };

    const filteredSolicitudes = getFilteredSolicitudes();

    // Group by conductor
    const groupedByConductor = filteredSolicitudes.reduce((acc, sol) => {
        const key = sol.conductor_id;
        if (!acc[key]) {
            acc[key] = {
                conductorId: key,
                nombre: sol.conductor_nombre || `Conductor #${key}`,
                solicitudes: []
            };
        }
        acc[key].solicitudes.push(sol);
        return acc;
    }, {});

    const conductorGroups = Object.values(groupedByConductor).filter(group =>
        group.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
                <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 lg:p-6" style={{ background: 'var(--bg-primary)' }}>
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                            Gestión de Asuntos Propios
                        </h1>
                        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                            Administrar solicitudes de A.P. de los conductores
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={loadData}
                            className="p-2 rounded-lg transition-colors"
                            style={{ background: 'var(--bg-tertiary)' }}
                            title="Actualizar"
                        >
                            <RefreshCw className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
                        </button>
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            className="px-3 py-2 rounded-lg border"
                            style={{
                                background: 'var(--bg-secondary)',
                                borderColor: 'var(--border-color)',
                                color: 'var(--text-primary)'
                            }}
                        >
                            {[2024, 2025, 2026].map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="rounded-xl p-4 border" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                        <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-4 h-4 text-amber-500" />
                            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Pendientes</span>
                        </div>
                        <p className="text-2xl font-bold text-amber-500">{pendientes.length}</p>
                    </div>
                    <div className="rounded-xl p-4 border" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                        <div className="flex items-center gap-2 mb-2">
                            <Check className="w-4 h-4 text-emerald-500" />
                            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Concedidas</span>
                        </div>
                        <p className="text-2xl font-bold text-emerald-500">
                            {allSolicitudes.filter(s => s.estado === 'concedido').length}
                        </p>
                    </div>
                    <div className="rounded-xl p-4 border" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                        <div className="flex items-center gap-2 mb-2">
                            <X className="w-4 h-4 text-red-500" />
                            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Denegadas</span>
                        </div>
                        <p className="text-2xl font-bold text-red-500">
                            {allSolicitudes.filter(s => s.estado === 'denegado').length}
                        </p>
                    </div>
                    <div className="rounded-xl p-4 border" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                        <div className="flex items-center gap-2 mb-2">
                            <FileText className="w-4 h-4 text-cyan-500" />
                            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Total</span>
                        </div>
                        <p className="text-2xl font-bold text-cyan-500">{allSolicitudes.length}</p>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <div className="relative w-full md:w-80">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                            type="text"
                            placeholder="Buscar conductor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                            style={{
                                background: 'var(--bg-card)',
                                borderColor: 'var(--border-color)',
                                color: 'var(--text-primary)'
                            }}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {[
                            { key: 'pendientes', label: 'Pendientes', color: 'amber' },
                            { key: 'concedidas', label: 'Concedidas', color: 'emerald' },
                            { key: 'denegadas', label: 'Denegadas', color: 'red' },
                            { key: 'todas', label: 'Todas', color: 'cyan' }
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setFilter(tab.key)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === tab.key
                                    ? `bg-${tab.color}-500/20 text-${tab.color}-500 border border-${tab.color}-500/50`
                                    : ''
                                    }`}
                                style={filter !== tab.key ? {
                                    background: 'var(--bg-secondary)',
                                    color: 'var(--text-secondary)'
                                } : undefined}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Solicitudes List */}
                {conductorGroups.length === 0 ? (
                    <div className="rounded-2xl border p-8 text-center" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                        <Search className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                        <p style={{ color: 'var(--text-secondary)' }}>
                            {searchTerm
                                ? `No se encontraron conductores que coincidan con "${searchTerm}"`
                                : `No hay solicitudes ${filter !== 'todas' ? filter : ''}`
                            }
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {conductorGroups.map(group => (
                            <div
                                key={group.conductorId}
                                className="rounded-2xl border overflow-hidden"
                                style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
                            >
                                {/* Conductor Header */}
                                <button
                                    onClick={() => setExpandedConductor(
                                        expandedConductor === group.conductorId ? null : group.conductorId
                                    )}
                                    className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                                            <User className="w-5 h-5 text-white" />
                                        </div>
                                        <div className="text-left">
                                            <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                                                {group.nombre}
                                            </p>
                                            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                                                {group.solicitudes.length} solicitud{group.solicitudes.length !== 1 ? 'es' : ''}
                                            </p>
                                        </div>
                                    </div>
                                    {expandedConductor === group.conductorId ? (
                                        <ChevronUp className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                                    ) : (
                                        <ChevronDown className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                                    )}
                                </button>

                                {/* Solicitudes */}
                                {expandedConductor === group.conductorId && (
                                    <div className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                                        {group.solicitudes.map(sol => (
                                            <div
                                                key={sol.id}
                                                className="p-4 border-b last:border-b-0 flex flex-col sm:flex-row sm:items-center gap-4"
                                                style={{ borderColor: 'var(--border-color)' }}
                                            >
                                                {/* Fecha */}
                                                <div className="flex items-center gap-3 flex-1">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center
                                                        ${sol.estado === 'pendiente' ? 'bg-amber-500/20' :
                                                            sol.estado === 'concedido' ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}
                                                    >
                                                        <Calendar className={`w-5 h-5
                                                            ${sol.estado === 'pendiente' ? 'text-amber-500' :
                                                                sol.estado === 'concedido' ? 'text-emerald-500' : 'text-red-500'}`}
                                                        />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium capitalize" style={{ color: 'var(--text-primary)' }}>
                                                            {formatFecha(sol.fecha)}
                                                        </p>
                                                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                                            {sol.es_arrastre && '📦 Día arrastrado • '}
                                                            {sol.created_at ? `Solicitado ${format(parseISO(sol.created_at), "d MMM", { locale: es })}` : 'Fecha de solicitud no disponible'}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Estado Badge */}
                                                <div className={`px-3 py-1 rounded-full text-sm font-medium
                                                    ${sol.estado === 'pendiente' ? 'bg-amber-500/20 text-amber-500' :
                                                        sol.estado === 'concedido' ? 'bg-emerald-500/20 text-emerald-500' :
                                                            'bg-red-500/20 text-red-500'}`}
                                                >
                                                    {sol.estado === 'pendiente' ? '⏳ Pendiente' :
                                                        sol.estado === 'concedido' ? '✓ Concedido' : '✗ Denegado'}
                                                </div>

                                                {/* Actions for pendientes */}
                                                {sol.estado === 'pendiente' && (
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => openNotaModal(sol.id, 'conceder')}
                                                            disabled={respondingId === sol.id}
                                                            className="px-4 py-2 rounded-lg bg-emerald-500/20 text-emerald-500 hover:bg-emerald-500/30 transition-colors flex items-center gap-2"
                                                        >
                                                            {respondingId === sol.id ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <Check className="w-4 h-4" />
                                                            )}
                                                            Conceder
                                                        </button>
                                                        <button
                                                            onClick={() => openNotaModal(sol.id, 'denegar')}
                                                            disabled={respondingId === sol.id}
                                                            className="px-4 py-2 rounded-lg bg-red-500/20 text-red-500 hover:bg-red-500/30 transition-colors flex items-center gap-2"
                                                        >
                                                            <X className="w-4 h-4" />
                                                            Denegar
                                                        </button>
                                                    </div>
                                                )}

                                                {/* Admin note if exists */}
                                                {sol.nota_admin && (
                                                    <div className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                                                        <MessageSquare className="w-4 h-4 mt-0.5" />
                                                        <span>{sol.nota_admin}</span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Nota Modal */}
            {showNotaModal && currentAction && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div
                        className="w-full max-w-md rounded-2xl p-6"
                        style={{ background: 'var(--bg-card)' }}
                    >
                        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                            {currentAction.action === 'conceder' ? '✅ Conceder A.P.' : '❌ Denegar A.P.'}
                        </h3>
                        <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                            Opcionalmente puedes añadir una nota para el conductor:
                        </p>
                        <textarea
                            value={notaAdmin}
                            onChange={(e) => setNotaAdmin(e.target.value)}
                            placeholder="Nota opcional..."
                            rows={3}
                            className="w-full px-4 py-3 rounded-xl border resize-none mb-4"
                            style={{
                                background: 'var(--bg-secondary)',
                                borderColor: 'var(--border-color)',
                                color: 'var(--text-primary)'
                            }}
                        />
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowNotaModal(false);
                                    setCurrentAction(null);
                                }}
                                className="flex-1 py-3 rounded-xl border"
                                style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleResponder(currentAction.id, currentAction.action, notaAdmin)}
                                disabled={respondingId === currentAction.id}
                                className={`flex-1 py-3 rounded-xl font-medium text-white ${currentAction.action === 'conceder'
                                    ? 'bg-emerald-500 hover:bg-emerald-600'
                                    : 'bg-red-500 hover:bg-red-600'
                                    }`}
                            >
                                {respondingId === currentAction.id ? (
                                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                                ) : (
                                    currentAction.action === 'conceder' ? 'Conceder' : 'Denegar'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AsuntosPropiosAdmin;
